import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../config/apiClient';
import { LoginContext } from './loginContext';

export const ModulosContext = createContext();

const MODULOS_DEFAULT = {
  gestion: false,
  club: false,
  academia: false,
  clubSenia: false,
  sacarTurnoRequiereLogin: false,
  academiaPagosMuevenCaja: false,
  academiaMercadoPagoActivo: false,
  recalculoTurnosDespuesClase: false,
  clubTurnosDuracionVariableAdmin: false,
  clubTurnosCobrosMuevenCaja: true,
};

export const ModulosProvider = ({ children }) => {
  const { user, isAuthenticated } = useContext(LoginContext);
  const [modulosActivos, setModulosActivos] = useState(MODULOS_DEFAULT);
  const [modulosLoaded, setModulosLoaded] = useState(false);

  const isAdmin =
    user?.tiu_descripcion?.toUpperCase() === 'ADMINISTRADOR' ||
    user?.usu_tipousuario === 1;

  const loadModulosConfig = async () => {
    if (!isAuthenticated) {
      setModulosActivos(MODULOS_DEFAULT);
      setModulosLoaded(true);
      return;
    }

    setModulosLoaded(false);
    try {
      if (!isAdmin) {
        const response = await apiClient.get('/public/portada');
        if (!response.ok) {
          setModulosActivos(MODULOS_DEFAULT);
          return;
        }
        const data = await response.json();
        const config = data?.config || {};
        setModulosActivos({
          gestion: false,
          club: Boolean(config.club),
          academia: Boolean(config.academia),
          clubSenia: config.clubSenia !== false,
          sacarTurnoRequiereLogin: Boolean(config.sacarTurnoRequiereLogin),
          academiaPagosMuevenCaja: Boolean(config.academiaPagosMuevenCaja),
          academiaMercadoPagoActivo: Boolean(config.academiaMercadoPagoActivo),
          recalculoTurnosDespuesClase: Boolean(config.recalculoTurnosDespuesClase),
          clubTurnosDuracionVariableAdmin: Boolean(config.clubTurnosDuracionVariableAdmin),
          clubTurnosCobrosMuevenCaja: config.clubTurnosCobrosMuevenCaja !== false,
        });
        return;
      }

      const response = await apiClient.get('/modulos/config');
      if (!response.ok) {
        setModulosActivos(MODULOS_DEFAULT);
        return;
      }
      const data = await response.json();
      setModulosActivos({
        gestion: Boolean(data.gestion),
        club: Boolean(data.club),
        academia: Boolean(data.academia),
        clubSenia: data.clubSenia !== false,
        sacarTurnoRequiereLogin: Boolean(data.sacarTurnoRequiereLogin),
        academiaPagosMuevenCaja: Boolean(data.academiaPagosMuevenCaja),
        academiaMercadoPagoActivo: Boolean(data.academiaMercadoPagoActivo),
        recalculoTurnosDespuesClase: Boolean(data.recalculoTurnosDespuesClase),
        clubTurnosDuracionVariableAdmin: Boolean(data.clubTurnosDuracionVariableAdmin),
        clubTurnosCobrosMuevenCaja: data.clubTurnosCobrosMuevenCaja !== false,
      });
    } catch (error) {
      console.error('Error al cargar configuración de módulos:', error);
      setModulosActivos(MODULOS_DEFAULT);
    } finally {
      setModulosLoaded(true);
    }
  };

  useEffect(() => {
    loadModulosConfig();
  }, [isAuthenticated, isAdmin, user?.id, user?.usu_id]);

  const value = useMemo(
    () => ({
      modulosActivos,
      modulosLoaded,
      isAdmin,
      reloadModulos: loadModulosConfig,
    }),
    [modulosActivos, modulosLoaded, isAdmin]
  );

  return (
    <ModulosContext.Provider value={value}>
      {children}
    </ModulosContext.Provider>
  );
};

export const useModulos = () => {
  const ctx = useContext(ModulosContext);
  if (!ctx) throw new Error('useModulos debe usarse dentro de ModulosProvider');
  return ctx;
};
